import React, { useState, useEffect, useContext } from "react";
import { Select, Container, Grid, TextField, MenuItem, FormControl, InputLabel, Box, Checkbox, FormControlLabel } from '@mui/material';
import ContactsAutoComplete from "../Contacts/ContactsAutoComplete";
import UsersAutoComplete from "../Users/UsersAutoComplete";
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';
import LeadsAutoComplete from "../Lead/LeadsAutoComplete";
import AccountsAutoComplete from "../Accounts/AccountsAutoComplete";
import { AuthContext } from '../../context/AuthContext';
import utc from 'dayjs/plugin/utc';


const EditForm = ({ onSubmit, taskData }) => {
    const { userInfo } = useContext(AuthContext);
    const [isSimpleUser, setIsSimpleUser] = useState(true);
    const [ownerId, setOwnerId] = useState('');
    const [subject, setSubject] = useState('');
    const [dueDate, setDueDate] = useState(dayjs());
    const [leadId, setLeadId] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [status, setStatus] = useState(0);
    const [priority, setPriority] = useState(0);
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [remindOn, setRemindOn] = useState(dayjs());
    const [description, setDescription] = useState('');
    const [taskEntityType, setTaskEntityType] = useState(0);
    const [shouldFetchSingleLead, setShouldFetchSingleLead] = useState(false);
    const [shouldFetchSingleContact, setShouldFetchSingleContact] = useState(false);
    const [shouldFetchSingleAccount, setShouldFetchSingleAccount] = useState(false);

    dayjs.extend(utc);

    useEffect(() => {
        if (taskData) {
            setOwnerId(taskData.ownerId || '');
            setSubject(taskData.subject || '');
            setDueDate(dayjs.utc(taskData.dueDate) || dayjs());
            setLeadId(taskData.leadId || null);
            setStatus(taskData.status || 0);
            setPriority(taskData.priority || 0);
            setReminderEnabled(taskData.reminderEnabled || false);
            setRemindOn(dayjs(taskData.remindOn) || dayjs());
            setDescription(taskData.description || '');
            setTaskEntityType(taskData.taskEntityType || 0);

            if(taskData.taskEntityType === 0 && taskData.leadId > 0) setShouldFetchSingleLead(true);
            if(taskData.taskEntityType === 1 && taskData.leadId > 0) setShouldFetchSingleContact(true);
            if(taskData.accountId) {
                setAccountId(taskData.accountId);
                setShouldFetchSingleAccount(true);
            }
        }
    }, [taskData]);

    useEffect(() => {
            if (userInfo) {
                if (userInfo?.roles?.includes('Admin') || userInfo?.roles?.includes('Manager')) {
                    setIsSimpleUser(false);
                }
            }
        }, [userInfo]);



    const handleCheckBox = (event) => {
        setReminderEnabled(event.target.checked);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Collect all form data
        const formData = {
            ownerId,
            subject,
            "dueDate": dueDate.utc().toISOString(),
            leadId,
            accountId,
            status,
            priority,
            reminderEnabled,
            remindOn,
            description
            // Collect other fields as needed
        };
        // Call onSubmit function passed as a prop
        onSubmit(formData);
    }
    const handleChange = (event) => {
        setTaskEntityType(Number(event.target.value));
        if (event.target.value == 0) {
            setAccountId(null);
        }
        setLeadId(null);
    };

    return (
        <form id='create-form' onSubmit={handleSubmit}>
            <Container maxWidth="md" sx={{ marginTop: "10px" }}>
                <h4>Task Information</h4>
                <Grid container spacing={3}>
                    {/* Lead Owner */}
                    <Grid item xs={12} sm={6}>
                        <UsersAutoComplete setUserId={setOwnerId} userId={ownerId} disabled={isSimpleUser} />
                    </Grid>

                    {/* Subject */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Subject" required
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)} />
                    </Grid>

                    <Grid item xs={12} sm={6}>

                        <FormControl>
                            <RadioGroup
                                row
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                                value={taskEntityType}
                                onChange={handleChange}
                            >
                                <FormControlLabel value={0} control={<Radio />} label="Lead" />
                                <FormControlLabel value={1} control={<Radio />} label="Contact" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Leads */}
                    <Grid item xs={12} sm={6}>
                        {taskEntityType === 0 ? (
                            <LeadsAutoComplete leadId={leadId} setLeadId={setLeadId} shouldFetchSingleLead={shouldFetchSingleLead} />
                        ) : (
                            <ContactsAutoComplete contactId={leadId} setContactId={setLeadId} shouldFetchSingleContact={shouldFetchSingleContact} />
                        )}
                    </Grid>

                    {/* account */}
                    <Grid item xs={12} sm={6} disabled>
                        <Box
                            sx={{
                                pointerEvents: taskEntityType !== 1 ? "none" : "auto",
                                opacity: taskEntityType !== 1 ? 0.5 : 1,
                            }}
                        >
                            <AccountsAutoComplete accountId={accountId} setAccountId={setAccountId} shouldFetchSingleAccount={shouldFetchSingleAccount} />
                        </Box>
                    </Grid>

                    {/* Due date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Due date"
                                fullWidth
                                value={dueDate}
                                onChange={(newDate) => setDueDate(newDate)}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" >
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value={0}>Not Started</MenuItem>
                                <MenuItem value={1}>Deferred</MenuItem>
                                <MenuItem value={2}>In Progress</MenuItem>
                                <MenuItem value={3}>Completed</MenuItem>
                                <MenuItem value={4}>Waiting for input</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Priority */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined" >
                            <InputLabel>Priority</InputLabel>
                            <Select
                                label="Priority"
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                            >
                                <MenuItem value={0}>High</MenuItem>
                                <MenuItem value={1}>Highest</MenuItem>
                                <MenuItem value={2}>Low</MenuItem>
                                <MenuItem value={3}>Lowest</MenuItem>
                                <MenuItem value={4}>Normal</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Reminder Enabled */}
                    {/* <Grid item xs={12} sm={6}>
                        <FormControlLabel control={<Checkbox checked={reminderEnabled} onChange={handleCheckBox} />} label="Reminder" />
                    </Grid> */}

                    {/* Reminder date */}
                    {/* <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                disabled={!reminderEnabled}
                                label="Reminder date"
                                fullWidth
                                value={remindOn}
                                onChange={(newDate) => setRemindOn(newDate)}
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid> */}

                </Grid>
                <br />
                <h4>Description Information</h4>
                <Grid container spacing={3}>
                    {/* Description */}
                    <Grid item xs={12} sm={12}>
                        <TextField multiline fullWidth label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)} />
                    </Grid>
                </Grid >

            </Container >
        </form>
    )
}

export default EditForm;